import { AlertBar, Button, Typography } from '@packages/ui/shared';
import { useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/** Style for the alert bar inside the back-navigation modal. */
const alertBarStyle = { marginBottom: 24 };

/**
 * Props for the BackModal component.
 *
 * Controls visibility, loading state, and user actions for the
 * back-navigation confirmation modal.
 */
export type BackModalProps = {
    /** The alert bar message to display inside the modal (empty string hides it). */
    alertBarMessage: string;

    /** Whether the draft is currently being saved (disables buttons, shows spinner). */
    isDraftSaving: boolean;

    /** Whether this modal is currently visible. */
    isVisible: boolean;

    /** Called when the user dismisses the modal alert bar. */
    onAlertBarClose: () => void;

    /** Called when the user clicks Cancel to close the modal without action. */
    onCancel: () => void;

    /** Called when the user clicks "Exit without saving" to navigate away without saving. */
    onExitWithoutSaving: () => void;

    /** Called when the user clicks "Save and exit" to save the draft and navigate away. */
    onSaveAndExit: () => void;
};

/**
 * Back-navigation confirmation modal for the CID-CVN worksheet subview.
 * Offers the user three choices: exit without saving, cancel, or save and exit.
 *
 * @param props - component props
 * @returns the back-navigation modal
 */
export function BackModal({
    alertBarMessage,
    isDraftSaving,
    isVisible,
    onAlertBarClose,
    onCancel,
    onExitWithoutSaving,
    onSaveAndExit,
}: BackModalProps) {
    // Hooks
    const { t } = useTranslation();

    // Handlers
    const handleExitWithoutSavingButtonPress = useCallback(() => {
        onExitWithoutSaving();
    }, [onExitWithoutSaving]);

    return (
        <Modal centered onHide={onCancel} show={isVisible}>
            <Modal.Header>
                <Modal.Title>{t('techline:exitCaseModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <AlertBar error={true} onClose={onAlertBarClose} show={Boolean(alertBarMessage)} style={alertBarStyle}>
                    <Typography>{alertBarMessage}</Typography>
                </AlertBar>
                <Typography>{t('techline:exitCaseModal.message')}</Typography>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <div>
                        <Button
                            disabled={isDraftSaving}
                            onPress={handleExitWithoutSavingButtonPress}
                            variant="ghost-blue"
                        >
                            {t('buttons:exitWithoutSaving')}
                        </Button>
                    </div>
                    <div className="d-flex gap-1">
                        <Button disabled={isDraftSaving} onPress={onCancel} variant="ghost-blue">
                            {t('buttons:cancel')}
                        </Button>
                        <Button
                            disabled={isDraftSaving}
                            isLoading={isDraftSaving}
                            onPress={onSaveAndExit}
                            variant="primary"
                        >
                            {t('buttons:saveAndExit')}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
